// Snowflake ID Decoder Implementation
// Supports both Twitter and Discord Snowflake formats
// References:
// - Twitter: https://developer.twitter.com/en/docs/twitter-ids
// - Discord: https://discord.com/developers/docs/reference#snowflakes

// Twitter epoch: November 4, 2010, 01:42:54 UTC
const TWITTER_EPOCH = 1288834974657n;

// Discord epoch: January 1, 2015, 00:00:00 UTC
const DISCORD_EPOCH = 1420070400000n;

// Discord launched in May 2015
const DISCORD_LAUNCH_DATE = new Date('2015-05-01').getTime();

export interface SnowflakeDecodeResult {
  isValid: boolean;
  snowflakeId?: string;
  timestamp?: number;
  timestampHex?: string;
  date?: string;
  datacenterId?: number;
  workerId?: number;
  sequence?: number;
  binary?: string;
  platform?: 'Twitter' | 'Discord';
  isAmbiguous?: boolean;
  alternativePlatform?: 'Twitter' | 'Discord';
  error?: string;
}

/**
 * Checks if a snowflake ID could be validly interpreted as both Twitter and Discord
 */
function isAmbiguous(id: bigint): boolean {
  const timestampOffset = id >> 22n;
  const twitterTimestamp = Number(timestampOffset + TWITTER_EPOCH);
  const discordTimestamp = Number(timestampOffset + DISCORD_EPOCH);
  const now = Date.now();
  const oneMonthFromNow = now + (30 * 24 * 60 * 60 * 1000);

  // Both interpretations must give valid dates
  const twitterValid = twitterTimestamp >= DISCORD_LAUNCH_DATE && twitterTimestamp <= oneMonthFromNow;
  const discordValid = discordTimestamp >= DISCORD_LAUNCH_DATE && discordTimestamp <= oneMonthFromNow;

  return twitterValid && discordValid;
}

/**
 * Determines which platform's epoch to use for decoding
 * Returns the appropriate epoch, platform name, and ambiguity info
 */
function detectPlatformEpoch(id: bigint, preferredPlatform?: 'Twitter' | 'Discord'): {
  epoch: bigint;
  platform: 'Twitter' | 'Discord';
  isAmbiguous: boolean;
  alternativePlatform?: 'Twitter' | 'Discord';
} {
  const timestampOffset = id >> 22n;

  // Calculate timestamps with both epochs
  const twitterTimestamp = Number(timestampOffset + TWITTER_EPOCH);
  const discordTimestamp = Number(timestampOffset + DISCORD_EPOCH);
  const now = Date.now();
  const oneMonthFromNow = now + (30 * 24 * 60 * 60 * 1000);

  // Check if ambiguous
  const ambiguous = isAmbiguous(id);

  // If user specified a preferred platform and it's valid, use it
  if (preferredPlatform && ambiguous) {
    return {
      epoch: preferredPlatform === 'Twitter' ? TWITTER_EPOCH : DISCORD_EPOCH,
      platform: preferredPlatform,
      isAmbiguous: true,
      alternativePlatform: preferredPlatform === 'Twitter' ? 'Discord' : 'Twitter'
    };
  }

  // If Twitter interpretation gives a date before Discord launched (May 2015),
  // it must be a Twitter snowflake (Discord didn't exist yet)
  if (twitterTimestamp < DISCORD_LAUNCH_DATE) {
    return {
      epoch: TWITTER_EPOCH,
      platform: 'Twitter',
      isAmbiguous: false
    };
  }

  // If Discord interpretation gives a date after Discord launched
  // and before 1 month in the future, prefer Discord
  // (Discord is more commonly used now for recent IDs)
  if (discordTimestamp >= DISCORD_LAUNCH_DATE && discordTimestamp <= oneMonthFromNow) {
    return {
      epoch: DISCORD_EPOCH,
      platform: 'Discord',
      isAmbiguous: ambiguous,
      alternativePlatform: ambiguous ? 'Twitter' : undefined
    };
  }

  // Otherwise, use Twitter epoch (for older IDs after 2015 or edge cases)
  return {
    epoch: TWITTER_EPOCH,
    platform: 'Twitter',
    isAmbiguous: ambiguous,
    alternativePlatform: ambiguous ? 'Discord' : undefined
  };
}

/**
 * Validates if a string is a valid Snowflake ID format
 * Snowflake IDs are 64-bit integers, typically 18-19 digits
 */
export function isValidSnowflake(input: string): boolean {
  if (!input) {
    return false;
  }

  // Must be numeric only
  if (!/^\d+$/.test(input)) {
    return false;
  }

  // Snowflake IDs are typically 18-19 digits, but can be less for very old IDs
  // Minimum reasonable length is 10 digits (covers IDs from early 2010s)
  // Maximum is 19 digits (max safe 64-bit number as string)
  if (input.length < 10 || input.length > 19) {
    return false;
  }

  try {
    const id = BigInt(input);

    // Must be positive
    if (id <= 0n) {
      return false;
    }

    // Must fit in 63 bits (signed 64-bit integer, top bit unused)
    if (id >= (1n << 63n)) {
      return false;
    }

    // Detect which platform and get the appropriate epoch
    const { epoch } = detectPlatformEpoch(id);

    // Extract timestamp and validate it's reasonable
    const timestamp = (id >> 22n) + epoch;
    const timestampMs = Number(timestamp);

    // Timestamp should be after the detected epoch and before far future
    const now = Date.now();
    const farFuture = now + (365 * 24 * 60 * 60 * 1000 * 10); // 10 years from now
    const minTimestamp = Number(epoch);

    if (timestampMs < minTimestamp || timestampMs > farFuture) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Decodes a Snowflake ID string and extracts its components
 * @param input - The snowflake ID string to decode
 * @param forcePlatform - Optional platform to force interpretation as (for ambiguous IDs)
 */
export function decodeSnowflake(input: string, forcePlatform?: 'Twitter' | 'Discord'): SnowflakeDecodeResult {
  if (!input) {
    return {
      isValid: false,
      error: 'Please enter a Snowflake ID'
    };
  }

  const trimmedInput = input.trim();

  if (!trimmedInput) {
    return {
      isValid: false,
      error: 'Please enter a Snowflake ID'
    };
  }

  // Check if it's numeric
  if (!/^\d+$/.test(trimmedInput)) {
    return {
      isValid: false,
      error: 'Invalid Snowflake ID: Must contain only digits'
    };
  }

  if (!isValidSnowflake(trimmedInput)) {
    if (trimmedInput.length < 10) {
      return {
        isValid: false,
        error: `Invalid Snowflake ID length: ${trimmedInput.length} digits (minimum 10)`
      };
    }
    if (trimmedInput.length > 19) {
      return {
        isValid: false,
        error: `Invalid Snowflake ID length: ${trimmedInput.length} digits (maximum 19)`
      };
    }
    return {
      isValid: false,
      error: 'Invalid Snowflake ID: Timestamp out of valid range'
    };
  }

  try {
    const id = BigInt(trimmedInput);

    // Detect which platform and get the appropriate epoch
    const { epoch, platform, isAmbiguous: ambiguous, alternativePlatform } = detectPlatformEpoch(id, forcePlatform);

    // Extract components using bitwise operations
    // Snowflake ID structure (64 bits):
    // - 1 bit: unused (always 0)
    // - 41 bits: timestamp (milliseconds since epoch)
    // - 10 bits: machine ID (5 bits datacenter + 5 bits worker)
    // - 12 bits: sequence number

    // Extract timestamp (top 41 bits after the unused bit)
    const timestampOffset = id >> 22n;
    const timestampBigInt = timestampOffset + epoch;
    const timestamp = Number(timestampBigInt);

    // Extract datacenter ID (5 bits)
    const datacenterId = Number((id >> 17n) & 0b11111n);

    // Extract worker ID (5 bits)
    const workerId = Number((id >> 12n) & 0b11111n);

    // Extract sequence number (12 bits)
    const sequence = Number(id & 0b111111111111n);

    // Convert timestamp to date
    const date = new Date(timestamp).toISOString();

    // Convert timestamp to hex for display
    const timestampHex = timestampBigInt.toString(16).toUpperCase().padStart(12, '0');

    // Create binary representation for detailed view
    const binary = id.toString(2).padStart(64, '0');

    return {
      isValid: true,
      snowflakeId: trimmedInput,
      timestamp,
      timestampHex,
      date,
      datacenterId,
      workerId,
      sequence,
      binary,
      platform,
      isAmbiguous: ambiguous,
      alternativePlatform,
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Failed to decode Snowflake ID: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Formats the decode result for display
 */
export function formatSnowflakeResult(result: SnowflakeDecodeResult): string {
  if (!result.isValid) {
    return `❌ ${result.error || 'Invalid Snowflake ID'}`;
  }

  const machineId = ((result.datacenterId ?? 0) << 5) | (result.workerId ?? 0);
  const epoch = result.platform === 'Discord' ? DISCORD_EPOCH : TWITTER_EPOCH;
  const msSinceEpoch = (result.timestamp ?? 0) - Number(epoch);
  const platformEmoji = result.platform === 'Discord' ? '🎮' : '🐦';
  const learnMoreUrl = result.platform === 'Discord'
    ? 'https://discord.com/developers/docs/reference#snowflakes'
    : 'https://developer.twitter.com/en/docs/twitter-ids';

  const lines = [
    '✅ Valid Snowflake ID',
    '',
    `${platformEmoji} Platform: ${result.platform}`,
  ];

  if (result.isAmbiguous && result.alternativePlatform) {
    lines.push(`ℹ️  Note: This ID could also be interpreted as ${result.alternativePlatform}`);
  }

  lines.push(
    `Snowflake ID: ${result.snowflakeId}`,
    '',
    '📅 Timestamp Information:',
    `  Timestamp (ms): ${result.timestamp}`,
    `  Timestamp (hex): 0x${result.timestampHex}`,
    `  Date (ISO): ${result.date}`,
    `  Since ${result.platform} epoch: ${msSinceEpoch} ms`,
    '',
    '🖥️ Machine Information:',
    `  Machine ID: ${machineId}`,
    `  Datacenter ID: ${result.datacenterId}`,
    `  Worker ID: ${result.workerId}`,
    '',
    '🔢 Sequence & Binary:',
    `  Sequence: ${result.sequence}`,
    `  Binary: ${result.binary}`,
    '',
    `📖 Learn more: ${learnMoreUrl}`
  );

  return lines.join('\n');
}
